(() => {
  if (customElements.get('cart-discount')) return;

  const normalize = (code) => String(code || '').trim().toUpperCase();
  const cartFingerprint = (cart) => JSON.stringify([cart.currency, cart.total_price, cart.total_discount,
    cart.item_count, (cart.items || []).map((item) => [item.key, item.quantity, item.final_line_price]), cart.discount_codes]);
  const components = () => [...document.querySelectorAll('cart-discount')];
  const root = window.Shopify?.routes?.root || '/';
  let currentCart;
  let readPromise;
  let busy = false;
  let notice = null;
  let revision = 0;
  let refreshTimer;
  let needsSectionRefresh = false;
  const locked = new Map();

  function allocations(cart) {
    const savings = new Map();
    const codeAmounts = new Map();
    const add = (application, amount) => {
      if (!application?.title || !Number.isSafeInteger(amount) || amount <= 0) return;
      const key = normalize(application.title);
      const previous = savings.get(key);
      savings.set(key, { title: application.title, amount: (previous?.amount || 0) + amount });
      if (application.type === 'discount_code') {
        const code = normalize(application.code || application.title);
        codeAmounts.set(code, (codeAmounts.get(code) || 0) + amount);
      }
    };
    (cart.cart_level_discount_applications || []).forEach((entry) =>
      add(entry.discount_application || entry, entry.amount ?? entry.total_allocated_amount));
    (cart.items || []).forEach((item) => (item.line_level_discount_allocations || [])
      .forEach((allocation) => add(allocation.discount_application, allocation.amount)));
    return { savings, codeAmounts };
  }

  function setCart(cart) {
    if (!Array.isArray(cart?.items) || !Number.isSafeInteger(cart.total_price)) throw new Error();
    currentCart = cart;
    revision += 1;
    components().forEach((component) => component.render(cart));
    return cart;
  }

  async function readCart() {
    if (readPromise) return readPromise;
    readPromise = (async () => {
      const response = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(12000) });
      if (!response.ok) throw new Error();
      const cart = await response.json();
      if (!Array.isArray(cart.items) || !Array.isArray(cart.discount_codes)) throw new Error();
      return cart;
    })();
    try { return await readPromise; } finally { readPromise = null; }
  }

  function showNotice(key, kind = '', detail = '') {
    notice = { key, kind, detail };
    components().forEach((component) => component.renderNotice());
  }

  function lock(value) {
    busy = value;
    if (value) {
      document.querySelectorAll('cart-items, button[name="checkout"], input[name="checkout"]').forEach((node) => {
        const attribute = node.matches('cart-items') ? 'inert' : 'disabled';
        locked.set(node, { attribute, present: node.hasAttribute(attribute) });
        node.setAttribute(attribute, '');
      });
    } else {
      locked.forEach(({ attribute, present }, node) => { if (!present) node.removeAttribute(attribute); });
      locked.clear();
    }
    components().forEach((component) => component.setBusy(value));
  }

  function targets() {
    return [
      { id: 'mini-cart', section: document.getElementById('mini-cart')?.id, selector: '.shopify-section' },
      { id: 'main-cart-items', section: document.getElementById('main-cart-items')?.dataset.id, selector: '.js-contents' },
      { id: 'main-cart-footer', section: document.getElementById('main-cart-footer')?.dataset.id, selector: '.js-contents' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'mobile-cart-icon-bubble', section: 'mobile-cart-icon-bubble', selector: '.shopify-section' }
    ].filter((target) => target.section && document.getElementById(target.id));
  }

  async function renderSections(cart, shouldRender = () => true) {
    const renderTargets = targets();
    const ids = [...new Set(renderTargets.map((target) => target.section))];
    let sections = cart.sections || {};
    if (ids.some((id) => typeof sections[id] !== 'string')) {
      const url = new URL(window.location.href);
      url.searchParams.delete('section_id');
      url.searchParams.set('sections', ids.join(','));
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(12000) });
      if (!response.ok) throw new Error();
      sections = await response.json();
    }
    const patches = renderTargets.map((target) => {
      if (typeof sections[target.section] !== 'string') throw new Error();
      const html = new DOMParser().parseFromString(sections[target.section], 'text/html');
      const fresh = html.querySelectorAll(target.selector);
      const node = document.getElementById(target.id);
      const existing = node.querySelectorAll(target.selector);
      if (!fresh.length || (existing.length && existing.length !== fresh.length)) throw new Error();
      return { node, existing, fresh };
    });
    if (!shouldRender()) return false;
    const drafts = components().map((component) => {
      const input = component.querySelector('input');
      return { section: component.dataset.sectionId, value: input.value,
        focused: document.activeElement === input, start: input.selectionStart, end: input.selectionEnd };
    });
    patches.forEach(({ node, existing, fresh }) => {
      const textareas = [...node.querySelectorAll('textarea')].map((input) => ({ id: input.id, value: input.value }));
      const openDetails = [...node.querySelectorAll('details')].map((details) => details.open);
      if (existing.length) existing.forEach((element, index) => { element.innerHTML = fresh[index].innerHTML; });
      else node.innerHTML = fresh[0].innerHTML;
      textareas.forEach(({ id, value }) => {
        const input = id && node.querySelector(`#${CSS.escape(id)}`);
        if (input) input.value = value;
      });
      node.querySelectorAll('details').forEach((details, index) => { details.open = openDetails[index] || false; });
    });
    document.querySelector('cart-items')?.classList.toggle('is-empty', cart.item_count === 0);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', cart.item_count === 0);
    const footerId = document.getElementById('main-cart-footer')?.dataset.id;
    if (footerId && sections[footerId]) {
      const html = new DOMParser().parseFromString(sections[footerId], 'text/html');
      const shipping = document.querySelector('#main-cart-footer .free-shipping');
      const fresh = html.querySelector('.free-shipping');
      if (shipping && fresh) shipping.innerHTML = fresh.innerHTML;
    }
    drafts.forEach((draft) => {
      const input = components().find((component) => component.dataset.sectionId === draft.section)?.querySelector('input');
      if (!input) return;
      input.value = draft.value;
      if (draft.focused && !busy) {
        input.focus({ preventScroll: true });
        input.setSelectionRange(draft.start, draft.end);
      }
    });
    needsSectionRefresh = false;
    return true;
  }

  async function changeDiscount(component, code, remove) {
    if (busy) return;
    if (!code) { showNotice('empty', 'error'); component.querySelector('input').focus(); return; }
    if (!remove && code.includes(',')) { showNotice('single', 'error'); return; }
    if (window.LRBlackFridayGifts?.busy?.() || document.querySelector('.cart__items--disabled')) {
      showNotice('updating');
      return;
    }
    const sectionId = component.dataset.sectionId;
    lock(true);
    notice = null;
    components().forEach((element) => element.renderNotice());
    let cart;
    try {
      if (readPromise) await readPromise.catch(() => {});
      cart = await readCart();
      const codes = new Map(cart.discount_codes.map((entry) => [normalize(entry.code), entry.code]));
      if (remove) codes.delete(normalize(code));
      else codes.set(normalize(code), code);
      needsSectionRefresh = true;
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST', credentials: 'same-origin', signal: AbortSignal.timeout(12000),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: [...codes.values()].join(','),
          sections: [...new Set(targets().map((target) => target.section))],
          sections_url: window.location.pathname + window.location.search })
      });
      const result = await response.json();
      if (!response.ok || result.errors) {
        const error = new Error();
        error.detail = typeof result.description === 'string' ? result.description : '';
        throw error;
      }
      cart = Array.isArray(result.discount_codes) ? result : await readCart();
      setCart(cart);
      await renderSections(cart);
      setCart(cart);
      const entry = cart.discount_codes.find((item) => normalize(item.code) === normalize(code));
      if (remove) showNotice(entry ? 'error' : 'removed', entry ? 'error' : '');
      else if (entry?.applicable === true) {
        showNotice(allocations(cart).codeAmounts.has(normalize(entry.code)) ? 'applied' : 'pending');
      } else showNotice('unavailable', 'error');
      const active = components().find((element) => element.dataset.sectionId === sectionId);
      if (active && (remove || entry?.applicable === true)) active.querySelector('input').value = '';
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, source: 'cart-discount' } }));
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cart });
      }
    } catch (error) {
      try {
        cart = await readCart();
        setCart(cart);
        await renderSections(cart);
        setCart(cart);
      } catch {
        showNotice('error', 'error', error.detail);
        return;
      }
      showNotice('error', 'error', error.detail);
    } finally {
      lock(false);
      components().find((element) => element.dataset.sectionId === sectionId)?.querySelector('input')?.focus({ preventScroll: true });
    }
  }

  function scheduleRefresh(refreshSections = false) {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(async () => {
      if (busy || !components().length) return;
      const startedAt = revision;
      try {
        const cart = await readCart();
        if (busy || startedAt !== revision) return;
        notice = null;
        const changed = needsSectionRefresh || !currentCart || cartFingerprint(currentCart) !== cartFingerprint(cart);
        if (refreshSections && changed && !await renderSections(cart, () => !busy && startedAt === revision)) return;
        if (busy || startedAt !== revision) return;
        setCart(cart);
      } catch { showNotice('error', 'error'); }
    }, 80);
  }

  customElements.define('cart-discount', class CartDiscount extends HTMLElement {
    connectedCallback() {
      const decoder = document.createElement('textarea');
      this.messages = JSON.parse(this.querySelector('[data-discount-messages]').textContent, (key, value) => {
        if (typeof value !== 'string') return value;
        decoder.innerHTML = value;
        return decoder.value;
      });
      this.controller = new AbortController();
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-discount-remove]');
        if (remove || event.target.closest('[data-discount-apply]')) {
          event.preventDefault();
          void changeDiscount(this, remove ? remove.dataset.discountRemove : this.querySelector('input').value.trim(), Boolean(remove));
        }
      }, { signal: this.controller.signal });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.target.matches('input')) {
          event.preventDefault();
          void changeDiscount(this, event.target.value.trim(), false);
        }
      }, { signal: this.controller.signal });
      const initial = JSON.parse(this.querySelector('[data-discount-initial]').textContent);
      this.render(currentCart || initial);
      this.setBusy(busy);
      if (!busy) scheduleRefresh();
    }

    disconnectedCallback() { this.controller?.abort(); }

    setBusy(value) {
      this.setAttribute('aria-busy', String(value));
      this.querySelectorAll('input, button').forEach((element) => { element.disabled = value; });
    }

    renderNotice() {
      const status = this.querySelector('[data-discount-status]');
      status.textContent = notice ? [this.messages[notice.key], notice.detail].filter(Boolean).join(' ') : '';
      status.dataset.kind = notice?.kind || '';
      status.hidden = !notice;
    }

    render(cart) {
      const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: cart.currency || 'USD'
      }).format(cents / 100);
      const { savings, codeAmounts } = allocations(cart);
      const fingerprint = JSON.stringify([cart.discount_codes || [], [...savings.values()]]);
      if (fingerprint === this.fingerprint) {
        this.renderNotice();
        this.setBusy(busy);
        return;
      }
      this.fingerprint = fingerprint;
      const codes = this.querySelector('[data-discount-codes]');
      codes.replaceChildren();
      (cart.discount_codes || []).forEach((entry) => {
        const row = document.createElement('li');
        const text = document.createElement('span');
        text.textContent = entry.code;
        const status = document.createElement('small');
        status.textContent = entry.applicable !== true ? this.messages.not_applied
          : codeAmounts.has(normalize(entry.code)) ? '' : this.messages.checkout;
        if (status.textContent) { text.append(document.createElement('br'), status); }
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'cart-discount__remove';
        remove.dataset.discountRemove = entry.code;
        remove.setAttribute('aria-label', `${this.messages.remove} ${entry.code}`);
        remove.innerHTML = '<svg viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="m1 1 10 10M11 1 1 11" stroke="currentColor" stroke-width="1.5"/></svg>';
        row.append(text, remove);
        codes.append(row);
      });
      codes.hidden = !codes.children.length;
      const amounts = this.querySelector('[data-discount-savings]');
      amounts.replaceChildren();
      savings.forEach((saving) => {
        const row = document.createElement('li');
        const title = document.createElement('span');
        title.textContent = saving.title;
        const amount = document.createElement('span');
        amount.className = 'cart-discount__amount';
        amount.textContent = `−${money(saving.amount)}`;
        row.append(title, amount);
        amounts.append(row);
      });
      amounts.hidden = !amounts.children.length;
      this.renderNotice();
      this.setBusy(busy);
    }
  });

  document.addEventListener('cart:updated', (event) => {
    if (event.detail?.source === 'cart-discount' || busy) return;
    let cart = event.detail?.cart;
    try { if (typeof cart === 'string') cart = JSON.parse(cart); } catch { cart = null; }
    notice = null;
    if (Array.isArray(cart?.discount_codes)) {
      setCart(cart);
      if (event.detail?.source === 'product-offers') void renderSections(cart).then(() => setCart(cart)).catch(() => scheduleRefresh(true));
    } else scheduleRefresh();
  });
  ['cart:refresh', 'ajaxProduct:added'].forEach((name) => document.addEventListener(name, () => scheduleRefresh(true)));
  document.addEventListener('cartdrawer:opened', () => scheduleRefresh(true));
  window.addEventListener('pageshow', (event) => { if (event.persisted) scheduleRefresh(true); });
  window.addEventListener('focus', () => scheduleRefresh(true));
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => { if (event?.source !== 'cart-discount') scheduleRefresh(); });
  }
})();
